import { Fragment, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { materialsApi, lookupsApi, manpowerApi, usersApi, projectsApi, sageApi, type Material } from '@/lib/apiClient';
import { StateView, Button, Combobox } from '@/components';
import { fmtDate } from '@/lib/dates';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { SageSyncPanel } from '@/modules/sage/SageSyncPanel';
import ShippingDrawer from '../shipping/ShippingDrawer';

// Materials (CMS expansion): the PM/field view of every material a job needs —
// what's ordered, committed, arriving, late — sitting in front of Sage.
// Tracker-owned + manual entry today; the Sage sync (PO numbers + committed
// cost inbound) and the Cost Tracker roll-up land later, so the banner copy
// describes the design intent, not a live pipe.

// ─── Status pipeline (structural — FE owns labels/colors/order) ─────────────
const STAGES = [
  { key: 'requisition', label: 'Requisition',    short: 'REQUISITION',    color: 'var(--ew-n-500)',           bg: 'var(--ew-n-100)' },
  { key: 'po',          label: 'PO Issued',      short: 'PO ISSUED',      color: 'var(--ew-quality-700)',     bg: 'var(--ew-quality-50)' },
  { key: 'fab',         label: 'In Fabrication', short: 'IN FABRICATION', color: 'var(--ew-performance-700)', bg: 'var(--ew-performance-50)' },
  { key: 'pickup',      label: 'Pickup',         short: 'PICKUP',         color: 'var(--ew-safety-700)',      bg: 'var(--ew-safety-50)' },
  { key: 'shipped',     label: 'Shipped',        short: 'SHIPPED',        color: 'var(--ew-performance-700)', bg: 'var(--ew-performance-100)' },
  { key: 'onsite',      label: 'On Site',        short: 'ON SITE',        color: 'var(--ew-green-700)',       bg: 'var(--ew-green-50)' },
  { key: 'installed',   label: 'Installed',      short: 'INSTALLED',      color: '#fff',                      bg: 'var(--ew-green-700)' },
] as const;
type StageKey = typeof STAGES[number]['key'];
const stageMeta = (s: string) => STAGES.find((x) => x.key === s) ?? STAGES[0];

// Statuses at/after which the material is physically on the job — arrival
// and past-need-by flags no longer apply.
const ARRIVED: string[] = ['onsite', 'installed'];

// ─── Derived flags (client-side; the server stores only facts) ──────────────
function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

interface Flags { late: boolean; orderNow: boolean; arriving: boolean }
function flagsFor(m: Material): Flags {
  const du = daysUntil(m.needByDate);
  const arrived = ARRIVED.includes(m.status);
  return {
    late: du !== null && du < 0 && !arrived,
    orderNow: m.status === 'requisition' && du !== null && du <= 14,
    arriving: du !== null && du >= 0 && du <= 14 && !arrived,
  };
}

function money(v: number | null | undefined): string {
  if (v == null) return '—';
  return '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Leading number of the commitment's free-text qty ("460 CY" → 460). */
function qtyTotal(qty: string | null): number | null {
  const m = (qty ?? '').match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : null;
}

const css = `
  .mat-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .mat-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .mat-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .mat-table tr:last-child td { border-bottom: 0; }
  .mat-table tr.flagged td:first-child { box-shadow: inset 3px 0 0 var(--ew-safety); }
  .mat-muted { color: var(--ew-n-500); }
  .mat-code {
    font-family: var(--ew-font-mono, ui-monospace, monospace); font-size: 12px;
    background: var(--ew-n-100); color: var(--ew-n-600); border-radius: 4px; padding: 2px 6px; white-space: nowrap;
  }
  .mat-po {
    font-family: var(--ew-font-mono, ui-monospace, monospace); font-size: 12px;
    background: var(--ew-quality-50); color: var(--ew-quality-700); border-radius: 4px; padding: 2px 6px; white-space: nowrap;
  }
  .mat-badge {
    display: inline-flex; align-items: center; gap: 6px; border-radius: var(--ew-r-pill);
    padding: 3px 10px; font: var(--ew-fw-semibold) 11px/1.4 var(--ew-font); white-space: nowrap;
  }
  .mat-flag {
    display: inline-block; margin-left: 8px; border-radius: var(--ew-r-pill); padding: 2px 8px;
    font: var(--ew-fw-semibold) 10px/1.5 var(--ew-font); text-transform: uppercase; letter-spacing: .04em;
    background: var(--ew-safety-50); color: var(--ew-safety-700); border: 1px solid var(--ew-safety-100);
  }
  .mat-flag.late { background: var(--ew-danger-50); color: var(--ew-danger-700); border-color: var(--ew-danger-100); }
  .mat-pill {
    border: 1px solid var(--ew-n-200); background: #fff; color: var(--ew-n-600); cursor: pointer;
    border-radius: var(--ew-r-pill); padding: 6px 14px; font: var(--ew-fw-medium) 12px/1 var(--ew-font);
    transition: all var(--ew-dur-fast) var(--ew-ease);
  }
  .mat-pill:hover { border-color: var(--ew-n-400); }
  .mat-pill.active { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .mat-stage { flex: 1; min-width: 0; text-align: center; padding: 12px 6px; border-radius: var(--ew-r-md); }
  .mat-stage .n { font: var(--ew-fw-semibold) 22px/1.1 var(--ew-font); font-variant-numeric: tabular-nums; }
  .mat-stage .l { font: var(--ew-fw-semibold) 10px/1 var(--ew-font); letter-spacing: .06em; margin-top: 6px; }
  .mat-stage-arrow { color: var(--ew-n-300); font-size: 14px; align-self: center; flex: 0 0 auto; }
  .mat-expand {
    display: inline-flex; align-items: center; gap: 5px; cursor: pointer;
    border: 1px solid var(--ew-n-200); background: #fff; color: var(--ew-n-600);
    border-radius: var(--ew-r-pill); padding: 2px 9px; font: var(--ew-fw-medium) 11px/1.5 var(--ew-font);
    transition: border-color var(--ew-dur-fast) var(--ew-ease), color var(--ew-dur-fast) var(--ew-ease);
  }
  .mat-expand:hover { border-color: var(--ew-n-400); color: var(--ew-dark); }
  .mat-lines { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) 12.5px var(--ew-font); }
  .mat-lines td { padding: 5px 8px; border-top: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
`;

/** The QA/QC roll-up chips on a material line (concrete pours, rock deliveries). */
const ROLLUP_CHIP: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid var(--ew-quality-100)', background: 'var(--ew-quality-50)',
  color: 'var(--ew-quality-700)', borderRadius: 'var(--ew-r-pill)', padding: '2px 9px', font: 'var(--ew-fw-semibold) 11px/1.5 var(--ew-font)', cursor: 'pointer',
};

export default function MaterialsTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const qc = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const openQaqc = () => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set('tab', 'qaqc');
    return next;
  });

  const query = useQuery({
    queryKey: ['materials', projectId],
    queryFn: () => materialsApi.list({ projectId }),
  });

  // Same admin visibility toggle the Cost tab uses — hides every $ figure.
  const { data: config } = useQuery({ queryKey: ['manpower', 'config'], queryFn: manpowerApi.config, staleTime: 60_000 });
  const showCosts = config?.showProjectCosts === '1';

  const { data: costCodes } = useQuery({ queryKey: ['cost-codes'], queryFn: () => lookupsApi.listCostCodes(), staleTime: 5 * 60 * 1000 });

  // Job identity for the drawer's locked-project chip (warm workspace cache).
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string; title: string }>,
    staleTime: 60_000,
  });

  // Mirrors the backend guard (writes need 'materials.edit'); Admin short-circuits.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  // Activity-locked (SYSTAC / ClosedWon) projects can't take material requests
  // until a Sage job number lands — read-only regardless of permission.
  const canEdit = ((me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('materials.edit') ?? false)) && !locked;
  // Asking Logistics to collect a line is a Shipping request, which has its own permission (receiving.edit), so
  // someone can edit lines and not request, or the reverse. Same lock: a job with no Sage number takes no requests.
  const canRequestPickup = ((me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('receiving.edit') ?? false)) && !locked;

  const [filter, setFilter] = useState<'all' | 'arriving' | 'action'>('all');
  const [drawer, setDrawer] = useState<{ item: Material | null } | null>(null);
  /** The line a pickup request is being raised for: the Shipping drawer, prefilled from it. */
  const [pickupFor, setPickupFor] = useState<Material | null>(null);
  // Going straight to another job keeps this tab mounted: drop the line, or coming back to the first job would reopen it.
  useEffect(() => { setPickupFor(null); }, [projectId]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const rows = query.data ?? [];
  const derived = useMemo(() => rows.map((m) => ({ m, flags: flagsFor(m) })), [rows]);

  const kpis = useMemo(() => ({
    committed: rows.reduce((s, m) => s + (m.committedCost ?? 0), 0),
    open: rows.filter((m) => m.status !== 'installed').length,
    arriving: derived.filter((d) => d.flags.arriving).length,
    action: derived.filter((d) => d.flags.late || d.flags.orderNow).length,
  }), [rows, derived]);

  const stageCounts = useMemo(() => {
    const c: Record<StageKey, number> = { requisition: 0, po: 0, fab: 0, pickup: 0, shipped: 0, onsite: 0, installed: 0 };
    for (const m of rows) if (m.status in c) c[m.status as StageKey]++;
    return c;
  }, [rows]);

  const visible = derived.filter(({ flags }) =>
    filter === 'all' ? true :
    filter === 'arriving' ? flags.arriving :
    (flags.late || flags.orderNow));

  const onSaved = () => {
    qc.invalidateQueries({ queryKey: ['materials', projectId] });
    qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
    qc.invalidateQueries({ queryKey: ['cost-codes'] }); // free-entered codes join the lookup
    setDrawer(null);
  };

  const kpiTiles: { label: string; value: string; accent: string; hidden?: boolean }[] = [
    { label: 'Committed · Materials', value: money(kpis.committed), accent: 'var(--ew-green)', hidden: !showCosts },
    { label: 'Open Orders',           value: String(kpis.open),     accent: 'var(--ew-quality)' },
    { label: 'Arriving ≤ 14 Days',    value: String(kpis.arriving), accent: 'var(--ew-performance)' },
    { label: 'Needs Action',          value: String(kpis.action),   accent: 'var(--ew-safety)' },
  ];

  return (
    <>
      <style>{css}</style>

      {/* ── Sage sync status (read-only FROM Intacct). The "Sync now" control
          lives in Admin › Integrations now; this tab just shows how fresh the
          data is + keeps "+ Add Material" here. ── */}
      <SageSyncPanel
        variant="status"
        rightSlot={canEdit ? <button className="btn btn-primary" onClick={() => setDrawer({ item: null })}>+ Add Material</button> : undefined}
      />

      {/* ── KPI tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${kpiTiles.filter(t => !t.hidden).length}, minmax(0,1fr))`, gap: 'var(--ew-s-3)', marginBottom: 'var(--ew-s-4)' }}>
        {kpiTiles.filter((t) => !t.hidden).map((t) => (
          <div key={t.label} className="pw-kpi">
            <span className="pw-kpi-strip" style={{ background: t.accent }} />
            <span className="pw-kpi-label">{t.label}</span>
            <span className="pw-kpi-value">{t.value}</span>
          </div>
        ))}
      </div>

      {/* ── Pipeline ── */}
      <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
        <h2 className="pw-card-hd">Material Pipeline</h2>
        <div style={{ display: 'flex', gap: 6, padding: 'var(--ew-s-4)', alignItems: 'stretch' }}>
          {STAGES.map((s, i) => {
            const n = stageCounts[s.key];
            const muted = n === 0;
            return (
              <div key={s.key} style={{ display: 'contents' }}>
                {i > 0 && <span className="mat-stage-arrow">›</span>}
                <div className="mat-stage" style={{ background: muted ? 'var(--ew-n-50, #FAFAFA)' : s.bg, opacity: muted ? 0.65 : 1 }}>
                  <div className="n" style={{ color: muted ? 'var(--ew-n-400)' : s.color }}>{n}</div>
                  <div className="l" style={{ color: muted ? 'var(--ew-n-400)' : s.color }}>{s.short}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="pw-card">
        <h2 className="pw-card-hd" style={{ gap: 'var(--ew-s-3)' }}>
          <span>Materials</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, textTransform: 'none', letterSpacing: 0 }}>
            <button className={`mat-pill${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>All</button>
            <button className={`mat-pill${filter === 'arriving' ? ' active' : ''}`} onClick={() => setFilter('arriving')}>Arriving ≤ 14d</button>
            <button className={`mat-pill${filter === 'action' ? ' active' : ''}`} onClick={() => setFilter('action')}>Needs action</button>
          </div>
        </h2>
        <StateView query={query} empty="No materials tracked for this job yet.">
          {() => visible.length === 0 ? (
            <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
              Nothing matches this filter.
            </div>
          ) : (
            <div className="ew-table-scroll">
              <table className="mat-table ew-cards">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Cost Code</th>
                    <th>Vendor</th>
                    <th>Qty</th>
                    {showCosts ? <th style={{ textAlign: 'right' }}>Committed</th> : null}
                    <th>Sage PO #</th>
                    <th>Need By</th>
                    <th>Status</th>
                    {canEdit ? <th aria-label="Actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {visible.map(({ m, flags }) => {
                    const meta = stageMeta(m.status);
                    const du = daysUntil(m.needByDate);
                    const expanded = expandedId === m.id;
                    const canExpand = !!m.sagePoNumber;
                    return (
                      <Fragment key={m.id}>
                      <tr className={flags.late || flags.orderNow ? 'flagged' : ''}>
                        <td data-label="Material">
                          <div style={{ fontWeight: 500 }}>
                            {m.item}
                            {flags.late && <span className="mat-flag late">Past need-by</span>}
                            {!flags.late && flags.orderNow && <span className="mat-flag">Order now</span>}
                          </div>
                          {m.spec && <div className="mat-muted" style={{ fontSize: 12, marginTop: 2 }}>{m.spec}</div>}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                            {canExpand && (
                              /* Expand to the exact Intacct PO lines that make up this row. */
                              <button className="mat-expand" aria-expanded={expanded} onClick={() => setExpandedId(expanded ? null : m.id)}>
                                {expanded ? '▾' : '▸'} PO line items
                              </button>
                            )}
                            {m.pourCount > 0 && (
                              /* Concrete roll-up: pour releases (QA/QC) against this commitment. */
                              <button
                                onClick={openQaqc}
                                style={ROLLUP_CHIP}
                                title="Pour releases against this commitment — open QA/QC"
                              >
                                {m.releasedCy != null ? `${m.releasedCy}${qtyTotal(m.qty) ? ` / ${qtyTotal(m.qty)}` : ''} CY released` : `${m.pourCount} release${m.pourCount === 1 ? '' : 's'}`}
                                {` · ${m.pourCount} pour${m.pourCount === 1 ? '' : 's'} · View pours`}
                              </button>
                            )}
                            {m.rockCount > 0 && (
                              /* Aggregate roll-up: rock deliveries released against this
                                 commitment. No CY: rock is ordered in loads or tons. */
                              <button
                                onClick={openQaqc}
                                style={ROLLUP_CHIP}
                                title="Rock / aggregate deliveries against this commitment — open QA/QC"
                              >
                                {`${m.rockCount} rock deliver${m.rockCount === 1 ? 'y' : 'ies'} · View deliveries`}
                              </button>
                            )}
                          </div>
                        </td>
                        <td data-label="Cost Code">{m.costCode ? <span className="mat-code" title={m.costCodeName ?? undefined}>{m.costCode}</span> : <span className="mat-muted">—</span>}</td>
                        <td className="mat-muted" data-label="Vendor">{m.vendor ?? '—'}</td>
                        <td className="mat-muted" data-label="Qty">{m.qty ?? '—'}</td>
                        {showCosts ? (
                          <td data-label="Committed" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(m.committedCost)}</td>
                        ) : null}
                        <td data-label="Sage PO #">{m.sagePoNumber ? <span className="mat-po">{m.sagePoNumber}</span> : <span className="mat-muted">—</span>}</td>
                        <td data-label="Need By" style={{ whiteSpace: 'nowrap', color: du !== null && du < 0 && !ARRIVED.includes(m.status) ? 'var(--ew-danger)' : undefined, fontWeight: du !== null && du < 0 && !ARRIVED.includes(m.status) ? 600 : undefined }}>
                          {fmtDate(m.needByDate)}
                        </td>
                        <td data-label="Status">
                          <span className="mat-badge" style={{ background: meta.bg, color: meta.color }}>
                            <span style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
                            {meta.label}
                          </span>
                          {/* Setting Pickup tells Logistics a pickup is coming (the API sends a short notice, #719), but
                              with no details: no dimensions, weight, truck or onsite contact. This is how they get
                              those: a Shipping request, prefilled from the line. It is a second message, by design. */}
                          {canRequestPickup && m.status === 'pickup' && (
                            <div style={{ marginTop: 6 }}>
                              <button className="mat-expand" style={{ whiteSpace: 'nowrap' }} onClick={() => setPickupFor(m)}
                                title="Moving a line to Pickup tells Logistics a pickup is coming, without the details. Raise a Shipping request to give them the dimensions, weight, truck and an onsite contact.">
                                Request pickup
                              </button>
                            </div>
                          )}
                        </td>
                        {canEdit ? (
                          <td>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button variant="secondary" size="sm" onClick={() => setDrawer({ item: m })}>Edit</Button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={7 + (showCosts ? 1 : 0) + (canEdit ? 1 : 0)} style={{ background: 'var(--ew-n-50)', padding: 0 }}>
                            <MaterialLinesDetail materialId={m.id} showCosts={showCosts} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </StateView>
        <div style={{ padding: '10px var(--ew-s-4)', borderTop: '1px solid var(--ew-n-100)', font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)' }}>
          Flags are derived from need-by dates — “Order now” means a requisition is inside its 14-day window; “Past need-by” means the date passed before the material arrived on site.
        </div>
      </div>

      {/* ── This job's untracked Intacct PO lines — one-click Track creates the
          material row from the Sage data (no retyping). Kept at the bottom so the
          job's tracked materials (KPIs → pipeline → table) lead the page. ── */}
      <SagePoLinesCard projectId={projectId} canEdit={canEdit} />

      {drawer && (
        <MaterialDrawer
          item={drawer.item}
          projectId={projectId}
          jobNumber={projectQuery.data?.jobNumber ?? ''}
          jobTitle={projectQuery.data?.title ?? ''}
          costCodes={costCodes ?? []}
          showCosts={showCosts}
          onClose={() => setDrawer(null)}
          onSaved={onSaved}
        />
      )}

      {/* Only for the job it was opened on: navigating straight to another job keeps this tab mounted, and the line (and its
          material id) belongs to the first job, so the drawer must not follow. */}
      {pickupFor && pickupFor.projectId === projectId && (
        <ShippingDrawer
          item={null}
          lockedProject={{ id: projectId, jobNumber: projectQuery.data?.jobNumber ?? '', title: projectQuery.data?.title ?? '' }}
          prefill={{
            kind: 'pickup',
            items: pickupFor.qty ? `${pickupFor.item} (${pickupFor.qty})` : pickupFor.item,
            fromLocation: pickupFor.vendor ?? undefined,
            materialId: pickupFor.id,
          }}
          onClose={() => setPickupFor(null)}
          // Land on the Shipping tab, where the new request is the thing to look at.
          onSaved={() => {
            setPickupFor(null);
            setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('tab', 'shipping'); return next; });
          }}
        />
      )}
    </>
  );
}

// ─── Add / Edit drawer (OrderDrawer pattern) ─────────────────────────────────
function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function MaterialDrawer({ item, projectId, jobNumber, jobTitle, costCodes, showCosts, onClose, onSaved }: {
  item: Material | null;
  projectId: number;
  jobNumber: string;
  jobTitle: string;
  costCodes: { code: string; name: string | null }[];
  showCosts: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const { accounts } = useMsal();
  const currentName = accounts[0]?.name ?? accounts[0]?.username ?? 'You';
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { onEscape: onClose });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [form, setForm] = useState<Record<string, string>>(() => ({
    item:          item?.item ?? '',
    spec:          item?.spec ?? '',
    costCode:      item?.costCode ?? '',
    vendor:        item?.vendor ?? '',
    qty:           item?.qty ?? '',
    committedCost: item?.committedCost != null ? String(item.committedCost) : '',
    sagePoNumber:  item?.sagePoNumber ?? '',
    needByDate:    toDateInputValue(item?.needByDate),
    status:        item?.status ?? 'requisition',
    notes:         item?.notes ?? '',
  }));

  const f = (k: string) => form[k] ?? '';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = useCallback(async () => {
    if (!f('item').trim()) { setSaveError('Item is required.'); return; }
    setSaving(true); setSaveError(null);
    const ne = (v: string) => (v.trim() === '' ? null : v.trim());
    const payload = {
      projectId,
      item: f('item').trim(),
      spec: ne(f('spec')),
      costCode: ne(f('costCode')),
      vendor: ne(f('vendor')),
      qty: ne(f('qty')),
      committedCost: f('committedCost').trim() === '' ? null : Number(f('committedCost')),
      sagePoNumber: ne(f('sagePoNumber')),
      needByDate: ne(f('needByDate')),
      status: f('status') || 'requisition',
      notes: ne(f('notes')),
    };
    try {
      if (item) await materialsApi.update(item.id, payload);
      else await materialsApi.create(payload);
      onSaved();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message ?? err.message);
    }
    setSaving(false);
  }, [form, item, projectId, onSaved]);

  const deleteMutation = useMutation({
    mutationFn: () => materialsApi.delete(item!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['materials', projectId] });
      qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
      onClose();
    },
  });

  const inp: React.CSSProperties = { border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--ew-dark)' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--ew-n-500)', marginBottom: 3, display: 'block' };
  const help: React.CSSProperties = { fontSize: 11, color: 'var(--ew-n-400)', marginTop: 3 };
  const fw: React.CSSProperties = { display: 'flex', flexDirection: 'column' };
  const g2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' };
  const sh = (title: string) => (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--ew-green)', borderBottom: '1px solid var(--ew-n-100)', paddingBottom: 6, marginBottom: 10, marginTop: 18 }}>
      {title}
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200 }} />
      <div ref={drawerRef} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 540, maxWidth: '100vw', background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--ew-n-100)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{item ? 'Edit Material' : 'Add Material'}</div>
            <div style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
              Requestor <span style={{ color: 'var(--ew-green)', fontWeight: 600 }}>{item ? (item.requestor?.displayName ?? '—') : currentName}</span>
              {jobNumber ? <> · Job <b style={{ fontWeight: 600 }}>#{jobNumber}</b> · {jobTitle}</> : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {item && (
              <button onClick={() => { if (confirm('Delete this material line?')) deleteMutation.mutate(); }}
                style={{ fontSize: 12, color: 'var(--ew-danger)', border: '1px solid var(--ew-danger-100)', borderRadius: 'var(--ew-r-md)', padding: '5px 10px', background: '#fff', cursor: 'pointer' }}>
                Delete
              </button>
            )}
            <button onClick={onClose} style={{ fontSize: 12, color: 'var(--ew-n-500)', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '5px 10px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ fontSize: 12, background: 'var(--ew-green)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', padding: '6px 14px', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={onClose} aria-label="Close drawer" style={{ fontSize: 18, color: 'var(--ew-n-500)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
          </div>
        </div>

        {saveError && <div style={{ margin: '8px 18px 0', padding: '8px 12px', background: 'var(--ew-danger-50)', color: 'var(--ew-danger-700)', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{saveError}</div>}

        <div style={{ padding: '4px 18px 32px' }}>
          {sh('Material')}
          <div style={fw}>
            <label style={lbl}>Item *</label>
            <input style={inp} value={f('item')} onChange={set('item')} placeholder="e.g. W12×26 wide-flange beams" />
          </div>
          <div style={{ ...fw, marginTop: 8 }}>
            <label style={lbl}>Spec / Description</label>
            <input style={inp} value={f('spec')} onChange={set('spec')} placeholder="e.g. ASTM A992, shop-primed" />
          </div>
          <div style={{ ...g2, marginTop: 8 }}>
            <div style={fw}>
              <label style={lbl}>Cost Code</label>
              {/* Custom type-ahead over the full Sage standard list (~990
                  codes) — the native datalist popup was unstylable and clunky.
                  Free-typed codes are still accepted + remembered. */}
              <Combobox
                value={f('costCode')}
                onChange={(v) => setForm((p) => ({ ...p, costCode: v }))}
                options={costCodes.map((cc) => ({ value: cc.code, sub: cc.name ?? undefined }))}
                placeholder="Type a code or name…"
              />
              <span style={help}>
                {(() => {
                  const hit = costCodes.find((cc) => cc.code === f('costCode').trim());
                  return hit?.name ?? 'Sage standard list — free entry allowed';
                })()}
              </span>
            </div>
            <div style={fw}>
              <label style={lbl}>Vendor</label>
              <input style={inp} value={f('vendor')} onChange={set('vendor')} />
            </div>
            <div style={fw}>
              <label style={lbl}>Quantity</label>
              <input style={inp} value={f('qty')} onChange={set('qty')} placeholder="e.g. 42 tons" />
            </div>
            {showCosts && (
              <div style={fw}>
                <label style={lbl}>Committed Cost ($)</label>
                <input style={inp} type="number" min="0" step="0.01" value={f('committedCost')} onChange={set('committedCost')} />
                <span style={help}>Tracked against the job by cost code</span>
              </div>
            )}
          </div>

          {sh('Schedule & Status')}
          <div style={g2}>
            <div style={fw}>
              <label style={lbl}>Need-By Date</label>
              <input type="date" style={inp} value={f('needByDate')} onChange={set('needByDate')} />
              <span style={help}>When it must be on site — drives the Arriving & Needs-Action flags</span>
            </div>
            <div style={fw}>
              <label style={lbl}>Status</label>
              <select style={inp} value={f('status')} onChange={set('status')}>
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div style={fw}>
              <label style={lbl}>Sage PO #</label>
              <input style={inp} value={f('sagePoNumber')} onChange={set('sagePoNumber')} />
              <span style={help}>Leave blank until the PO is issued in Sage Intacct</span>
            </div>
          </div>

          {sh('Notes')}
          <div style={fw}>
            <textarea style={{ ...inp, minHeight: 80, resize: 'vertical' }} value={f('notes')} onChange={set('notes')} />
          </div>
        </div>
      </div>
    </>
  );
}


// ─── Expandable PO line items for a tracked material row ─────────────────────
// A tracked row owns one Intacct PO; this shows exactly which lines make it up
// (its material + sales-tax + freight, etc.) so nothing is hidden behind the
// single row. Cost is only shown when project costs are visible.
function MaterialLinesDetail({ materialId, showCosts }: { materialId: number; showCosts: boolean }) {
  const q = useQuery({
    queryKey: ['material-lines', materialId],
    queryFn: () => sageApi.materialLines(materialId),
    staleTime: 60_000,
  });
  const lines = q.data ?? [];
  if (q.isLoading) return <div style={{ padding: '8px var(--ew-s-4) 8px 28px', color: 'var(--ew-n-500)', fontSize: 12 }}>Loading PO lines…</div>;
  if (lines.length === 0) return <div style={{ padding: '8px var(--ew-s-4) 8px 28px', color: 'var(--ew-n-500)', fontSize: 12 }}>No Intacct PO lines on this row.</div>;
  return (
    <div style={{ padding: '6px var(--ew-s-4) 10px 28px' }}>
      <div style={{ font: 'var(--ew-fw-semibold) 10px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-n-500)', margin: '4px 0 6px' }}>
        PO {lines[0].docNo} · {lines.length} line{lines.length === 1 ? '' : 's'} from Intacct
      </div>
      <table className="mat-lines">
        <tbody>
          {lines.map((l) => (
            <tr key={l.recordNo}>
              <td style={{ whiteSpace: 'nowrap' }}>{l.costCode ? <span className="mat-code">{l.costCode}</span> : <span className="mat-muted">—</span>}</td>
              <td>
                {l.item ?? '—'}
                {l.memo ? <div className="mat-muted" style={{ fontSize: 11.5, marginTop: 2 }}>{l.memo}</div> : null}
              </td>
              <td className="mat-muted" style={{ whiteSpace: 'nowrap' }}>{l.qty ?? '—'}</td>
              {showCosts ? (
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{money(l.value)}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Untracked Intacct PO lines for this job ─────────────────────────────────
// The workflow flip (2026-07-17): PMs enter POs in Intacct — they should
// never retype them here. The sync surfaces each job's PO lines; "Track"
// creates the material row FROM the line (item / cost code / PO # / committed
// cost pre-filled). Curated on purpose: no auto-create, because PO lines
// include sales tax, equipment rental, etc. that the field doesn't track.
function SagePoLinesCard({ projectId, canEdit }: { projectId: number; canEdit: boolean }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const linesQuery = useQuery({
    queryKey: ['sage-lines', projectId],
    queryFn: () => sageApi.lines(projectId),
    staleTime: 60_000,
  });
  const trackMut = useMutation({
    mutationFn: (recordNo: number) => sageApi.track(recordNo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sage-lines', projectId] });
      qc.invalidateQueries({ queryKey: ['materials', projectId] });
      qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
      qc.invalidateQueries({ queryKey: ['cost-codes'] });
    },
  });
  const lines = linesQuery.data ?? [];

  // Default to material lines (Intacct cost type M, + CO change-order
  // material) — this is the MATERIALS tab; labor/subs/equipment-rental lines
  // are noise here (product owner). The toggle keeps everything reachable since the
  // cost-type coding has ragged edges (blanks, one-off types).
  const [scope, setScope] = useState<'materials' | 'all'>('materials');
  const isMaterialType = (ct: string | null) => ct === 'M' || ct === 'CO';
  const scoped = useMemo(
    () => (scope === 'all' ? lines : lines.filter((l) => isMaterialType(l.costType))),
    [lines, scope],
  );

  // Search across PO #, label, vendor, description, memo, and cost code —
  // labor-heavy jobs carry hundreds of lines (the product owner's ask).
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const searched = useMemo(() => (q === '' ? scoped : scoped.filter((l) =>
    [l.docNo, l.docLabel, l.vendor, l.item, l.memo, l.costCode]
      .some((v) => v != null && v.toLowerCase().includes(q)))), [scoped, q]);

  // Group by PO — one header row per document (label + vendor + total),
  // its lines nested beneath (product owner's review: repeated PO#s read as noise).
  const groups = useMemo(() => {
    const map = new Map<string, { docNo: string; docLabel: string | null; vendor: string | null; total: number; lines: typeof lines }>();
    for (const l of searched) {
      let g = map.get(l.docNo);
      if (!g) { g = { docNo: l.docNo, docLabel: l.docLabel, vendor: l.vendor, total: 0, lines: [] }; map.set(l.docNo, g); }
      g.docLabel = g.docLabel ?? l.docLabel;
      g.vendor = g.vendor ?? l.vendor;
      g.total += l.value ?? 0;
      g.lines.push(l);
    }
    return Array.from(map.values());
  }, [searched]);

  if (lines.length === 0) return null;
  // A search shows every hit; the 3-PO teaser only applies when browsing.
  const shownGroups = expanded || q !== '' ? groups : groups.slice(0, 3);

  return (
    <div className="pw-card" style={{ marginTop: 'var(--ew-s-6)', marginBottom: 'var(--ew-s-4)' }}>
      <h2 className="pw-card-hd" style={{ flexWrap: 'wrap', gap: 10 }}>
        Intacct POs · Not Tracked ({groups.length} POs · {searched.length} lines)
        <span style={{ font: 'var(--ew-fw-regular) 11px var(--ew-font)', color: 'var(--ew-n-400)', textTransform: 'none', letterSpacing: 0 }}>
          Track the lines the field cares about — the row is created from Sage, you only add dates & status
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className={`mat-pill${scope === 'materials' ? ' active' : ''}`} style={{ textTransform: 'none', letterSpacing: 0 }} onClick={() => setScope('materials')}
            title="Lines Intacct codes as material purchases (cost type M / CO)">Materials</button>
          <button className={`mat-pill${scope === 'all' ? ' active' : ''}`} style={{ textTransform: 'none', letterSpacing: 0 }} onClick={() => setScope('all')}
            title="Every PO line on the job — labor, subs, equipment rental, tax">All lines</button>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search PO #, description, vendor, cost code…"
            style={{
              border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)',
              padding: '6px 10px', font: 'var(--ew-fw-regular) 12px var(--ew-font)', width: 240,
              background: '#fff', color: 'var(--ew-dark)', textTransform: 'none', letterSpacing: 0,
            }}
          />
        </span>
      </h2>
      {groups.length === 0 ? (
        <div style={{ padding: 'var(--ew-s-4)', color: 'var(--ew-n-500)', fontSize: 13 }}>
          {q !== ''
            ? <>No untracked PO lines match “{search.trim()}”.</>
            : <>No untracked material lines on this job — switch to <b>All lines</b> for labor, subs & equipment.</>}
        </div>
      ) : null}
      {trackMut.isError ? (
        <div style={{ margin: '10px var(--ew-s-4) 0', padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>
          {(trackMut.error as Error).message}
        </div>
      ) : null}
      <div className="ew-table-scroll">
        <table className="mat-table">
          <tbody>
            {shownGroups.map((g) => (
              <FragmentGroup key={g.docNo} g={g} canEdit={canEdit} onTrack={(rec) => trackMut.mutate(rec)} tracking={trackMut.isPending} />
            ))}
          </tbody>
        </table>
      </div>
      {q === '' && groups.length > 3 ? (
        <div style={{ padding: '8px var(--ew-s-4)', borderTop: '1px solid var(--ew-n-100)' }}>
          <button className="ew-btn-link" style={{ color: 'var(--ew-green)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }} onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Show fewer' : `Show all ${groups.length} POs`}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** One PO group: header row (PO # · label · vendor · total), lines beneath. */
function FragmentGroup({ g, canEdit, onTrack, tracking }: {
  g: { docNo: string; docLabel: string | null; vendor: string | null; total: number; lines: import('@/lib/apiClient').SagePoLine[] };
  canEdit: boolean;
  onTrack: (recordNo: number) => void;
  tracking: boolean;
}) {
  return (
    <>
      <tr style={{ background: 'var(--ew-n-50)' }}>
        <td colSpan={canEdit ? 5 : 4} style={{ padding: '9px var(--ew-s-4)' }}>
          <span className="mat-po">{g.docNo}</span>
          {g.docLabel ? <span style={{ marginLeft: 10, fontWeight: 600 }}>{g.docLabel}</span> : null}
          {g.vendor ? <span className="mat-muted" style={{ marginLeft: 10 }}>{g.vendor}</span> : null}
          <span style={{ float: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(g.total)}</span>
        </td>
      </tr>
      {g.lines.map((l) => (
        <tr key={l.recordNo}>
          <td data-label="Cost Code" style={{ paddingLeft: 28, whiteSpace: 'nowrap' }}>
            {l.costCode ? <span className="mat-code">{l.costCode}</span> : <span className="mat-muted">—</span>}
          </td>
          <td data-label="Description">
            {l.item ?? '—'}
            {l.memo ? <div className="mat-muted" style={{ fontSize: 12, marginTop: 2 }}>{l.memo}</div> : null}
          </td>
          <td className="mat-muted" data-label="Qty" style={{ whiteSpace: 'nowrap' }}>{l.qty ?? '—'}</td>
          <td data-label="Value" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500, whiteSpace: 'nowrap' }}>{money(l.value)}</td>
          {canEdit ? (
            <td style={{ textAlign: 'right' }}>
              <Button variant="secondary" size="sm" onClick={() => onTrack(l.recordNo)} isLoading={tracking}>
                Track
              </Button>
            </td>
          ) : null}
        </tr>
      ))}
    </>
  );
}
